import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { exposeWhenVisible, track, variantOf } from '@theme/hds-experiments';

/**
 * Link die een adviespagina in een drawer laadt (SHO-10420).
 *
 * De pagina wordt pas bij klik (of bij hover/focus, als prefetch) opgehaald via de
 * Section Rendering API: `<url>?section_id=advice-drawer-content`. Uit het antwoord
 * nemen we alleen `[data-advice-drawer-content]` over. Mislukt het ophalen, dan
 * navigeren we gewoon naar de pagina — de link is en blijft een echte link.
 *
 * Met `data-stop-before="<handle>, <handle>"` toont de drawer alleen het deel vóór het
 * eerste blok dat voorkomt (bv. alleen de meetstappen, niet de FAQ en de standaardmaten).
 * De titel in de drawer is de linktekst (`data-heading`), niet de paginatitel; de link naar
 * de volledige pagina onderaan krijgt de tekst uit `data-page-link-intro`/`-label`.
 *
 * A/B-test (`data-experiment`): de link staat verborgen in de HTML en alleen de treatment-arm
 * krijgt hem te zien. De blootstelling telt in beide armen zodra dit element in beeld komt —
 * in de control-arm is dat de lege plek waar de link had gestaan. Openen en de klik naar de
 * volledige pagina gaan als `hds:advice_drawer_open` / `hds:advice_drawer_page_link` naar de
 * pixel, ook zonder experiment (dan zijn `experiment_id` en `variant` leeg).
 *
 * Toelichting (kenmerk-lade, specificatietabel): staat een `<template data-lead>` in het element,
 * dan komt die bovenaan de drawer, en de adviespagina (als die er is) daaronder onder een
 * kennisbank-kop. Zonder `data-page-url` toont de drawer alleen de toelichting — geen fetch.
 * `data-placement` zegt waar de link staat (default `buybox`) en gaat mee in de tracking.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLAnchorElement} trigger
 * @property {HTMLElement} inner
 * @property {HTMLElement} [loading]
 *
 * @extends DialogComponent
 */
class AdviceDrawerLink extends DialogComponent {
  requiredRefs = ['dialog', 'trigger', 'inner'];

  static SECTION_ID = 'advice-drawer-content';

  /** @type {Promise<string> | null} */
  #contentPromise = null;

  /** @type {AbortController | null} */
  #listeners = null;

  connectedCallback() {
    super.connectedCallback();

    const { trigger, inner } = this.refs;
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    // Directe listener i.p.v. `on:click`: vóór hydratie navigeert de klik gewoon naar de
    // pagina, en hds-early-interactions hoeft hem dan ook niet in de wachtrij te zetten.
    trigger.addEventListener('click', this.#handleTriggerClick, { signal });
    trigger.addEventListener('pointerenter', this.#prefetch, { signal });
    trigger.addEventListener('focus', this.#prefetch, { signal });
    // Altijd bovenaan beginnen, ook bij opnieuw openen met gecachete inhoud. Pas ná
    // showModal(): zolang de dialog dicht is, is de inhoud niet gerenderd en doet
    // scrollTop niets.
    this.addEventListener(DialogOpenEvent.eventName, this.#resetScroll, { signal });
    // De inhoud komt later via innerHTML; daarom gedelegeerd op de container.
    inner.addEventListener('click', this.#handlePageLinkClick, { signal });
    // Een video die speelt mag niet doorlopen als de drawer dicht is.
    this.refs.dialog.addEventListener('close', this.#stopMedia, { signal });

    this.#joinExperiment();
  }

  #resetScroll = () => {
    this.refs.inner.scrollTop = 0;
  };

  /** Naam van het experiment, leeg als de link gewoon altijd staat. */
  get experiment() {
    return this.dataset.experiment || '';
  }

  /**
   * Deelt de bezoeker in en toont de link alleen in de treatment-arm. De variant staat
   * meteen vast; het meetellen wacht tot dit element in beeld is (exposeWhenVisible), zodat
   * wie nooit tot de buybox scrolt niet in de noemer belandt.
   */
  #joinExperiment() {
    const { experiment } = this;
    if (!experiment) return;

    const variant = exposeWhenVisible(experiment, this, this.#context());
    if (variant === 'treatment') {
      this.refs.trigger.hidden = false;
    } else {
      // Control = de buybox zoals zonder dit blok: ook de ingestelde ruimte eromheen weg.
      this.style.setProperty('padding', '0');
    }
  }

  /** Velden die elk event meekrijgt: waar en bij welk product. */
  #context() {
    return {
      placement: this.dataset.placement || 'buybox',
      product_id: this.dataset.productId ?? '',
      guide: this.dataset.guide ?? '',
      // Specificatietabel: welke eigenschap (`bijzonderheden`) en welke waarde ("Natuurlijke baanwerking").
      ...(this.dataset.specName ? { spec_name: this.dataset.specName } : {}),
      ...(this.dataset.specValue ? { spec_value: this.dataset.specValue } : {}),
    };
  }

  /** @param {string} name */
  #track(name) {
    const { experiment } = this;
    track(name, {
      experiment_id: experiment,
      variant: experiment ? variantOf(experiment) : '',
      ...this.#context(),
    });
  }

  /** @param {MouseEvent} event */
  #handlePageLinkClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest('.advice-drawer__page-link a')) return;
    this.#track('hds:advice_drawer_page_link');
  };

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
    super.disconnectedCallback();
  }

  get pageUrl() {
    return this.dataset.pageUrl || this.refs.trigger.getAttribute('href') || '';
  }

  /** Toelichting die server-side is meegerenderd (kenmerk-lade), of null. */
  get lead() {
    return this.querySelector(':scope > template[data-lead]');
  }

  /** Handles (kommagescheiden) van de blokken waar de drawer-inhoud vóór stopt. */
  get stopBefore() {
    return (this.dataset.stopBefore || '')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);
  }

  /** @param {MouseEvent} event */
  #handleTriggerClick = (event) => {
    // Nieuw tabblad / venster: laat de browser zijn gang gaan.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    if (!this.pageUrl && !this.lead) return;

    event.preventDefault();
    this.showDialog();
    this.#track('hds:advice_drawer_open');
    this.#load();
  };

  #prefetch = () => {
    if (!this.#contentPromise && this.pageUrl) this.#fetchContent();
  };

  /**
   * Haalt de sectie op en houdt de belofte vast; een tweede klik gebruikt het cache.
   * @returns {Promise<string>}
   */
  #fetchContent() {
    if (this.#contentPromise) return this.#contentPromise;

    const url = new URL(this.pageUrl, window.location.origin);
    url.searchParams.set('section_id', AdviceDrawerLink.SECTION_ID);

    this.#contentPromise = fetch(url.toString(), {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`advice-drawer: ${response.status} voor ${url.pathname}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-advice-drawer-content]');
        if (!content) throw new Error('advice-drawer: geen [data-advice-drawer-content] in antwoord');
        // De paginatitel verdwijnt uit de kop (die wordt de linktekst), maar blijft nodig
        // als kop van het kennisbank-blok onder de toelichting.
        const guideTitle = content.querySelector('.advice-drawer__title')?.textContent?.trim() ?? '';
        this.#adapt(content);
        this.#deferVideos(content);
        this.#trim(content);
        this.#frame(content, guideTitle);
        return content.outerHTML;
      })
      .catch((error) => {
        // Niet cachen: een volgende poging mag opnieuw proberen.
        this.#contentPromise = null;
        throw error;
      });

    return this.#contentPromise;
  }

  /**
   * Maakt de pagina-inhoud passend voor het paneel. De titel wordt de linktekst: daar
   * klikte de klant op, en de paginatitel is voor Google geschreven ("… opmeten of
   * standaardmaat"). De link naar de volledige pagina krijgt de tekst uit het blok, zodat
   * hij kan zeggen wélke vragen daar nog beantwoord worden.
   * @param {Element} content
   */
  #adapt(content) {
    const { heading, pageLinkIntro, pageLinkLabel, hideFirstSectionTitle } = this.dataset;

    const title = content.querySelector('.advice-drawer__title');
    if (title && heading) title.textContent = heading;
    // De eerste sectiekop staat pal onder de drawertitel en zegt vaak hetzelfde.
    if (hideFirstSectionTitle === 'true') content.querySelector('.advice-element__section-title')?.remove();

    const pageLink = content.querySelector('.advice-drawer__page-link');
    if (!pageLink) return;
    const link = pageLink.querySelector('a');
    if (link && pageLinkLabel) link.textContent = pageLinkLabel;
    // Kenmerk-lade: de volledige pagina opent in een nieuw tabblad, met het bekende
    // «extern»-icoon (icon-external.svg, ook bij popup-link) en een tekst voor schermlezers.
    if (link && this.dataset.pageLinkExternal === 'true') {
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      const icon = this.querySelector(':scope > template[data-page-link-icon]');
      if (icon) link.append(icon.content.cloneNode(true));
      const hint = document.createElement('span');
      hint.className = 'visually-hidden';
      hint.textContent = ' (opent in een nieuw tabblad)';
      link.append(hint);
    }
    if (pageLinkIntro) {
      const intro = document.createElement('span');
      intro.className = 'advice-drawer__page-link-intro';
      intro.textContent = pageLinkIntro;
      pageLink.prepend(intro, ' ');
    }
  }

  /**
   * Knipt de inhoud af vóór het eerste blok uit `stopBefore`: dat blok en alles erna
   * verdwijnt, net als de pagina-intro. Geen match = hele pagina. De wrappers dragen `advice-drawer-element-<handle>`
   * als id (zie `sections/advice-drawer-content`).
   * @param {Element} content
   */
  #trim(content) {
    const handles = this.stopBefore;
    if (handles.length === 0) return;

    const wrappers = [...content.querySelectorAll('.advice-element-wrapper')];
    const index = wrappers.findIndex((wrapper) =>
      handles.includes(wrapper.id.replace(/^advice-drawer-element-/, ''))
    );
    if (index === -1) return;

    wrappers.slice(index).forEach((wrapper) => wrapper.remove());
    // De pagina-intro gaat over de hele pagina; bij een deel ervan is hij misleidend.
    content.querySelector('.advice-drawer__intro')?.remove();
    // Lege USP-rij of lege elementenlijst niet als witruimte achterlaten.
    content.querySelectorAll('.advice-content-elements__usps-run').forEach((run) => {
      if (!run.querySelector('.advice-element-wrapper')) run.remove();
    });
    const list = content.querySelector('.advice-drawer__elements');
    if (list && !list.querySelector('.advice-element-wrapper')) list.remove();
    content.setAttribute('data-trimmed', 'true');
  }

  /**
   * Een facade (video-facade.liquid) bewaart een echte `<video autoplay>` in een verborgen
   * blok. Op de pagina zelf blokkeert de browser geluid zonder gebaar; hier komt de inhoud
   * binnen vlak ná een tik, dus dan speelt die video wél af — met geluid, onder het
   * play-voorbeeld. Daarom: geen autoplay en niets laden tot de bezoeker op play drukt.
   * @param {Element} content
   */
  #deferVideos(content) {
    content.querySelectorAll('video[autoplay]').forEach((video) => {
      video.removeAttribute('autoplay');
      video.setAttribute('preload', 'none');
    });
  }

  /**
   * Het script van video-facade.liquid koppelt de playknop alleen bij het laden van de
   * pagina, dus niet voor inhoud die later in de drawer komt. Zelfde gedrag, hier opnieuw.
   * @param {Element} root
   */
  #wireFacades(root) {
    root.querySelectorAll('.video-facade[data-video-id]').forEach((wrapper) => {
      const facade = /** @type {HTMLElement | null} */ (wrapper.querySelector('.video-facade__facade'));
      const player = /** @type {HTMLElement | null} */ (wrapper.querySelector('.video-facade__player'));
      if (!facade || !player) return;

      facade.addEventListener('click', (event) => {
        event.preventDefault();
        if (facade.dataset.videoLoaded === 'true') return;
        facade.dataset.videoLoaded = 'true';
        facade.style.display = 'none';
        player.hidden = false;
        player.style.display = 'block';

        if (facade.dataset.videoMode === 'external') {
          const src = facade.dataset.embedSrc;
          if (!src) return;
          const iframe = document.createElement('iframe');
          iframe.src = src;
          iframe.title = facade.dataset.iframeTitle || '';
          iframe.className = 'video-facade__iframe';
          iframe.setAttribute('allow', 'autoplay; fullscreen');
          iframe.setAttribute('allowfullscreen', '');
          player.append(iframe);
          return;
        }

        const video = /** @type {HTMLVideoElement | null} */ (wrapper.querySelector('.video-facade__video'));
        video?.play().catch(() => {
          video.controls = true;
        });
      });
    });
  }

  /** Drawer dicht: video's pauzeren en ingesloten spelers weghalen, het play-voorbeeld terug. */
  #stopMedia = () => {
    const { inner } = this.refs;
    inner.querySelectorAll('video').forEach((video) => video.pause());
    inner.querySelectorAll('.video-facade__iframe').forEach((iframe) => {
      const wrapper = iframe.closest('.video-facade');
      iframe.remove();
      const facade = /** @type {HTMLElement | null} */ (wrapper?.querySelector('.video-facade__facade') ?? null);
      const player = /** @type {HTMLElement | null} */ (wrapper?.querySelector('.video-facade__player') ?? null);
      if (facade) {
        facade.style.display = '';
        facade.dataset.videoLoaded = 'false';
      }
      if (player) {
        player.hidden = true;
        player.style.display = '';
      }
    });
  };

  /**
   * Zet de toelichting onder de titel en de adviespagina daaronder in een eigen blok
   * («Uit onze kennisbank»). De link naar de volledige pagina blijft los onderaan staan, zodat
   * hij sticky kan zijn. Zonder toelichting verandert er niets aan de inhoud.
   * @param {Element} content
   * @param {string} [guideTitle]
   */
  #frame(content, guideTitle = '') {
    const lead = this.lead;
    if (!lead) return;

    // De link naar de volledige pagina is hier de vaste actie onderaan: als knop.
    content.querySelector('.advice-drawer__page-link a')?.classList.add('button', 'button-secondary');

    const title = content.querySelector('.advice-drawer__title');
    const fragment = /** @type {DocumentFragment} */ (lead.content.cloneNode(true));
    if (title) title.after(fragment);
    else content.prepend(fragment);

    const leadSection = content.querySelector('.advice-drawer__lead');
    const pageLink = content.querySelector('.advice-drawer__page-link');
    const guideNodes = [];
    for (let node = leadSection?.nextElementSibling ?? null; node; node = node.nextElementSibling) {
      if (node !== pageLink) guideNodes.push(node);
    }
    if (guideNodes.length === 0) return;

    const guide = document.createElement('section');
    guide.className = 'advice-drawer__guide';
    const eyebrow = document.createElement('p');
    eyebrow.className = 'advice-drawer__guide-eyebrow';
    eyebrow.textContent = this.dataset.guideLabel || 'Uit onze kennisbank';
    guide.append(eyebrow);
    if (guideTitle) {
      const heading = document.createElement('h3');
      heading.className = 'advice-drawer__guide-title h4';
      heading.textContent = guideTitle;
      guide.append(heading);
    }
    guide.append(...guideNodes);
    leadSection?.after(guide);
  }

  /** Drawer zonder adviespagina: alleen kop en toelichting. */
  #leadOnlyContent() {
    const content = document.createElement('div');
    content.className = 'advice-drawer__content';
    content.setAttribute('data-advice-drawer-content', '');
    const title = document.createElement('h2');
    title.className = 'advice-drawer__title h4';
    title.textContent = this.dataset.heading ?? '';
    content.append(title);
    this.#frame(content);
    return content.outerHTML;
  }

  async #load() {
    const { inner, dialog } = this.refs;
    if (inner.dataset.loaded === 'true') return;

    try {
      const html = this.pageUrl ? await this.#fetchContent() : this.#leadOnlyContent();
      inner.innerHTML = html;
      this.#wireFacades(inner);
      inner.dataset.loaded = 'true';
      inner.setAttribute('aria-busy', 'false');
      this.dispatchEvent(new CustomEvent('advice-drawer:loaded', { bubbles: true }));
    } catch (error) {
      console.warn(error);
      // Terugvallen op de echte pagina, alleen als de drawer nog open is.
      if (dialog.open) {
        await this.closeDialog();
        window.location.assign(this.pageUrl);
      }
    }
  }
}

if (!customElements.get('advice-drawer-link')) {
  customElements.define('advice-drawer-link', AdviceDrawerLink);
}
